// ExistingDocumentPicker for FileUploaderV2
// See .cursor/requirements/file-uploader-v2-plan.md for details

import { FocusEvent, useState } from 'react';

import { TypeaheadMultiSelectInput } from '@/components/form/baseInputs/TypeaheadMultiSelectInput/TypeaheadMultiSelectInput';
import { useExistingHouseholdDocumentOptions } from '@/modules/documents/MultiDocumentUploader/hooks/useExistingHouseholdDocumentOptions';
import { DocumentType } from '@/types/schema';

interface ExistingDocumentPickerProps {
  householdId: string;
  value: string[];
  onChange: (documents: { id: string; displayName: string }[]) => void;
  disabled?: boolean;
  excludedDocumentTypes?: DocumentType[];
}

export function ExistingDocumentPicker({
  householdId,
  value,
  onChange,
  disabled,
  excludedDocumentTypes,
}: ExistingDocumentPickerProps) {
  const [inputValue, setInputValue] = useState('');

  const { options, loading } = useExistingHouseholdDocumentOptions({
    householdId,
    skip: disabled,
    excludedDocumentTypes,
  });
  const selectedExistingDocumentOptions = options.filter((option) =>
    value.includes(option.value)
  );

  const inputHasNoOptions = options.length === 0 && !loading;

  const handleFocus = (e: FocusEvent) => {
    // without this, the event will trigger a parent's onClick handler
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <TypeaheadMultiSelectInput
        label="Use existing documents"
        disabled={disabled || inputHasNoOptions}
        hideLabel
        loading={loading}
        values={selectedExistingDocumentOptions}
        placeholder={
          inputHasNoOptions ? 'No existing documents' : 'Use existing documents'
        }
        maxTagDisplayLength={23}
        onFocus={handleFocus}
        onChange={(_e, values) => {
          onChange(
            values.map((v) => ({ id: v.value, displayName: v.display }))
          );
        }}
        options={options}
        inputValue={inputValue}
        onInputChange={setInputValue}
        // startAdornment doesn't work the MUI's Autocomplete component with `multiple` set because
        // apparently they use the startAdornment to render the "chips"
        // https://github.com/mui/material-ui/issues/22103
        // startAdornment={
        //   <InputAdornment position="start">
        //     <FileDownload02Icon size={18} color={COLORS.NAVY[700]} />
        //   </InputAdornment>
        // }
      />
    </div>
  );
}
